import { exactInteger } from '@/lib/exact'
import type {
  Block, Cell, ChartSeries, ChipCell, Column, KvItem, TableBlock, Tone,
} from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Platform Ops — all five screens, read from `/v1/admin/platform/*`
 * (go-wallet#233, handler `internal/server/admin_platform.go`).
 *
 *   `pl-cells`   ← GET /platform/cells
 *   `pl-workers` ← GET /platform/workers
 *   `pl-outbox`  ← GET /platform/outbox
 *   `pl-jobs`    ← GET /platform/jobs
 *   `pl-build`   ← GET /platform/build
 *
 * # One module, because they share one vocabulary and it must not fork
 *
 * Every one of these responses carries the same `cell`, the same two-valued
 * `status`, and the same three-valued `availability`. Split across five files,
 * each is a copy — and the copies would disagree in the direction that matters:
 * the whole point of `not_built` versus `unavailable` is that the console
 * renders them differently, and the fifth implementation of that distinction is
 * where one of them quietly becomes "—".
 *
 * # `degraded` is the worst thing any of these screens may say
 *
 * `PlatformStatus` has two values and deliberately no `down` (law 20). A deep
 * outbox or a lagging reconciler is stale REPORTING, drawn as a degraded-data
 * notice on the affected screens — never a service-down state, never a reason to
 * block a money screen, and never wired into readiness. Nothing here renders an
 * outage, because the backend cannot report one.
 *
 * # Zero workers on a standby is correct, and a console must not page for it
 *
 * A standby runs no workers by design. So on a standby `workers` is empty,
 * `runs_workers` is false, and `status` is `ok`. A console inferring an outage
 * from an empty list would page for a cell doing exactly what it is configured
 * to do — and, worse, teach its operators that the word means nothing.
 *
 * # Absent is not zero, in four separate places on these screens
 *
 * Each of these is a real figure that the backend deliberately OMITS rather than
 * sending as zero, and rendering the omission as `0` is the specific failure the
 * omission exists to prevent:
 *
 *   - **replication lag** — absent on a primary and on a replica that has
 *     replayed nothing. It is the number a human reads immediately before
 *     promoting a cell, and a reassuring zero meaning "not measured" is the
 *     worst value it could hold.
 *   - **a worker's heartbeat age** — absent for `never_started`, which is the
 *     ABSENCE OF A RUN ROW. A zero age puts a reconciler nobody deployed in the
 *     same row as a healthy idle one, which is go-wallet#88 exactly.
 *   - **outbox lag** — derived from the oldest undelivered row and nothing else.
 *     On an empty queue there is no oldest row, so there is no lag.
 *   - **a job's duration and rows touched** — absent on a run that never
 *     finished. Zero would read as "examined nothing" for a run that simply did
 *     not get there.
 *
 * # Nothing here is a control
 *
 * No promotion, no restart, no drain, no retry, no requeue. Promotion in
 * particular is a human decision with a stale-replica risk and is not a button
 * on a monitoring screen; where it exists at all it is a break-glass flow that
 * states the lag at the moment of confirmation. There is no such route on the
 * admin surface, and this console does not draw one.
 */

// ---------------------------------------------------------------------------
// The shared vocabulary
// ---------------------------------------------------------------------------

/** Whether a figure has a value at all. Three states, never two. */
export type Availability = 'available' | 'not_built' | 'unavailable'

export interface PlatformCellView {
  readonly id: string
  readonly role: string
  readonly accepts_writes: boolean
  readonly runs_workers: boolean
  readonly environment?: string
  readonly build?: string
}

interface CellFacts {
  readonly id: string
  readonly role: string
  readonly acceptsWrites: boolean | null
  readonly runsWorkers: boolean | null
  readonly environment: string
  readonly build: string
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function whole(value: unknown): number | null {
  return exactInteger(value)
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

function readCell(raw: unknown): CellFacts | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const c = raw as Partial<PlatformCellView>
  const id = text(c.id)
  if (id === '') return null
  return {
    id,
    role: text(c.role),
    acceptsWrites: bool(c.accepts_writes),
    runsWorkers: bool(c.runs_workers),
    environment: text(c.environment),
    build: text(c.build),
  }
}

const AVAILABILITIES: readonly string[] = ['available', 'not_built', 'unavailable']

function availabilityOf(value: unknown): Availability | null {
  const a = text(value)
  return AVAILABILITIES.includes(a) ? (a as Availability) : null
}

/**
 * The sentence for a figure that has no value, and its tone.
 *
 * `not_built` is a ROADMAP FACT and needs no operator — the Today board counts
 * it as needing no attention — so it is neutral. `unavailable` is an incident:
 * something that should have answered did not. Folding the two into one dash
 * turns "we have not built this yet" into "this is broken", which is how a
 * backlog item becomes a page at 3am.
 */
function absence(availability: Availability | null, reason: string): ChipCell {
  if (availability === 'not_built') {
    return [reason === '' ? 'not built yet' : `not built: ${reason}`, 'n']
  }
  if (availability === 'unavailable') {
    return [reason === '' ? 'could not be read' : `unreadable: ${reason}`, 'r']
  }
  return ['— availability not stated', 'r']
}

/**
 * Seconds as a span a human reads. Never rounded up.
 *
 * A lag of 119 seconds is `1m 59s` rather than `2m`: on the screen somebody
 * reads before promoting a cell, rounding is a small lie in the direction of
 * whichever number is easier to say.
 */
function span(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))}m ${String(seconds % 60)}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (seconds < 86400) return `${String(h)}h ${String(m)}m`
  return `${String(Math.floor(seconds / 86400))}d ${String(h % 24)}h`
}

/** `2026-07-31T14:01:58Z` → `2026-07-31 14:01:58`. */
function stamp(at: string): string {
  return at === '' ? '' : at.replace('T', ' ').replace(/(\.\d+)?Z$/, '')
}

/** The role chip. A standby is amber everywhere it appears, on every screen. */
function roleChip(role: string): ChipCell {
  if (role === 'primary') return ['PRIMARY', 'g']
  if (role === 'standby') return ['STANDBY', 'a']
  return [role === '' ? '— not stated' : `unknown: ${role}`, 'r']
}

/**
 * The status line every platform screen carries.
 *
 * Rendered as a note rather than a chip because the word on its own is the
 * problem: `degraded` beside a table means nothing until it says what is
 * degraded and what that costs. And `ok` is worth stating on a standby, where
 * an empty worker table would otherwise read as the opposite.
 */
function statusNote(status: string, cell: CellFacts | null, detail: string): Block {
  const degraded = status === 'degraded'
  return {
    t: 'note',
    tone: degraded ? 'a' : 'n',
    kicker: degraded ? 'Degraded' : 'Status',
    title: degraded
      ? 'Degraded — stale reporting, not an outage'
      : status === 'ok'
        ? 'Reporting normally'
        : 'The backend did not state a status',
    body: degraded
      ? `${detail} A degraded platform reads as stale data on the screens that depend on it. It is never a service-down state, never a reason to block a money screen, and is deliberately not wired into readiness — a readiness probe that fails on a queue depth takes a cell out for a reporting problem.`
      : `${detail} This surface has exactly two states and no "down": the failure it is designed around degrades reporting and leaves every money write untouched.`,
    ref:
      cell === null
        ? 'the answering cell was not stated'
        : `answered by ${cell.id} · ${cell.role}${cell.build === '' ? '' : ` · ${cell.build}`}`,
  }
}

/** Which cell answered, as rows an operator can read off any platform screen. */
function cellItems(cell: CellFacts | null): readonly KvItem[] {
  if (cell === null) {
    return [[
      'answering cell',
      '— not stated',
      'unread',
      'r',
      'Every platform response is supposed to carry the cell that produced it, so the console never has to remember which one it asked.',
      '',
    ]]
  }
  return [
    ['cell', cell.id, '', 'n', 'Asserted from required config, never inferred.', 'm'],
    [
      'role',
      cell.role === '' ? '— not stated' : cell.role,
      cell.role === 'standby' ? 'standby' : cell.role === 'primary' ? 'primary' : 'unknown',
      cell.role === 'standby' ? 'a' : cell.role === 'primary' ? 'g' : 'r',
      'A standby refuses every mutating request with 409, whether or not this console drew the control.',
      '',
    ],
    [
      'accepts writes',
      cell.acceptsWrites === null ? '— not stated' : cell.acceptsWrites ? 'yes' : 'no',
      '',
      cell.acceptsWrites === false ? 'a' : 'n',
      'The backend is the enforcement point; this is what it says about itself.',
      '',
    ],
    [
      'runs workers',
      cell.runsWorkers === null ? '— not stated' : cell.runsWorkers ? 'yes' : 'no — by design',
      '',
      'n',
      'A standby runs none. That is what stops an empty worker table reading as an outage.',
      '',
    ],
    [
      'environment',
      cell.environment === '' ? '— not stated' : cell.environment,
      '',
      cell.environment === 'production' ? 'a' : 'n',
      'Stated by the backend’s composition root rather than inferred from a hostname.',
      'm',
    ],
    [
      'build',
      cell.build === '' ? '— not stated' : cell.build,
      '',
      'n',
      'What this cell is actually running.',
      'm',
    ],
  ]
}

interface Envelope {
  readonly cell: CellFacts | null
  readonly status: string
  readonly generatedAt: string
}

function envelopeOf(payload: Record<string, unknown>): Envelope {
  return {
    cell: readCell(payload['cell']),
    status: text(payload['status']),
    generatedAt: text(payload['generated_at']),
  }
}

function provenanceOf(
  env: Envelope,
  source: string,
  missing: readonly string[],
  readAt: Date,
): DataSource {
  const readStamp =
    env.generatedAt === ''
      ? readAtStamp(readAt)
      : `${env.generatedAt.slice(0, 10)} ${env.generatedAt.slice(11, 16)} UTC`
  const authority = env.cell === null ? 'operational' : `operational · ${env.cell.id}`
  return missing.length === 0
    ? { kind: 'live', source, authority, readAt: readStamp }
    : { kind: 'partial', source, authority, readAt: readStamp, missing }
}

// ---------------------------------------------------------------------------
// pl-cells
// ---------------------------------------------------------------------------

const CELLS_SOURCE = 'go-wallet · this cell’s own config (GET /v1/admin/platform/cells)'

const CELL_COLS: readonly Column[] = [
  ['WALLET_CELL_ID', 'm', '170px'],
  ['WALLET_CELL_ROLE', 'c', '150px'],
  ['Environment', 'x', '130px'],
  ['Workers', 'c', '160px'],
  ['Writes', 'c', '140px'],
  ['Build', 'm', 'minmax(150px,1fr)'],
]

function cellRow(c: CellFacts): readonly Cell[] {
  return [
    c.id,
    roleChip(c.role),
    c.environment === '' ? '— not stated' : c.environment,
    c.runsWorkers === null
      ? (['— not stated', 'r'] as ChipCell)
      : c.runsWorkers
        ? (['running', 'g'] as ChipCell)
        : (['none by design', 'n'] as ChipCell),
    c.acceptsWrites === null
      ? (['— not stated', 'r'] as ChipCell)
      : c.acceptsWrites
        ? (['accepted', 'g'] as ChipCell)
        : (['refused', 'n'] as ChipCell),
    c.build === '' ? '— not stated' : c.build,
  ]
}

/**
 * The replication row.
 *
 * Absent lag is the case this exists for. On a primary there is nothing to
 * replay; on a replica that has replayed nothing there is no measurement. A
 * zero in either case is a reassuring number that means "not measured", read by
 * somebody deciding whether to promote.
 */
function replicationItems(raw: unknown): readonly KvItem[] {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return [['replication', '— not returned', 'unread', 'r', 'The backend did not report replication state at all.', '']]
  }
  const r = raw as Record<string, unknown>
  const availability = availabilityOf(r['availability'])
  const reason = text(r['reason'])
  const lag = whole(r['lag_seconds'])
  const inRecovery = bool(r['in_recovery'])
  const lastReplayed = text(r['last_replayed_at'])

  if (availability !== 'available') {
    const [label, tone] = absence(availability, reason)
    return [['replication lag', label, '', tone, 'A lag this console could not read is not a lag of zero.', '']]
  }

  return [
    [
      'in recovery',
      inRecovery === null ? '— not stated' : inRecovery ? 'yes — this is a replica' : 'no — this is not replaying',
      '',
      'n',
      'Whether this database is replaying a stream at all.',
      '',
    ],
    [
      'replication lag',
      lag === null
        ? '— not measured'
        : span(lag),
      lag === null ? 'unmeasured' : lag > 60 ? 'behind' : 'close',
      lag === null ? 'a' : lag > 60 ? 'a' : 'g',
      lag === null
        ? 'ABSENT rather than zero. A primary has nothing to replay, and a replica that has replayed nothing has nothing to measure — the number read immediately before a promotion must never be a reassuring zero that means "not measured".'
        : 'Measured from the replay stream. In-flight instructions may be absent from a replica this far behind.',
      '',
    ],
    [
      'last replayed',
      lastReplayed === '' ? '— not stated' : stamp(lastReplayed),
      '',
      'n',
      'When this replica last applied something.',
      'm',
    ],
  ]
}

const NO_PROMOTION: Block = {
  t: 'note',
  tone: 'r',
  kicker: 'Not a button',
  title: 'Promotion is not a control on this screen, and there is no route for it',
  body: 'Promoting a standby is a human decision with a stale-replica risk: at any real lag, in-flight instructions may be absent from the replica. Where a promotion control exists at all it is a break-glass flow that states the lag at the moment of confirmation and makes the operator type the cell id. No automatic health check triggers it, and the admin surface exposes no route this console could call.',
  ref: 'the lag above is the number that decision turns on',
}

function peerNote(raw: unknown): Block {
  const p = typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const availability = availabilityOf(p['availability'])
  const reason = text(p['reason'])
  return {
    t: 'note',
    tone: 'n',
    kicker: 'One cell, by construction',
    title: 'This response describes the cell that answered, and nothing about the other one',
    body: 'Cross-cell traffic is asynchronous only, so this process has no way to ask its peer anything — a console that renders both cells does it by polling both. The single row above is the shape of the answer rather than a single-cell deployment.',
    ref:
      availability === 'available'
        ? reason === '' ? 'the peer is reported as readable' : reason
        : reason === ''
          ? 'no peer state is reported'
          : reason,
  }
}

export function composeCells(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const env = envelopeOf(payload)
  const raw = Array.isArray(payload['cells']) ? (payload['cells'] as readonly unknown[]) : []
  const cells: CellFacts[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readCell(entry)
    if (parsed === null) unreadable += 1
    else cells.push(parsed)
  }

  const table: TableBlock = {
    t: 'table',
    title: 'Cells',
    src: 'ledger',
    facets: false,
    cols: CELL_COLS,
    rows: cells.map(cellRow),
    footer:
      unreadable === 0
        ? `${String(cells.length)} cell(s) — the one that answered`
        : `${String(cells.length)} cell(s) · ${String(unreadable)} could not be read`,
    foot2:
      'A standby runs no workers and refuses every mutating request. Both are correct states, not failures — and both are asserted from required config, never inferred.',
  }

  const missing: string[] = []
  if (env.cell === null) missing.push('which cell answered')
  if (cells.length === 0) missing.push('the cell registry')

  return {
    blocks: [
      statusNote(env.status, env.cell, 'This is the cell topology as this process asserts it.'),
      table,
      // The cell that ANSWERED, stated separately from the registry above.
      // They are the same cell today and that is the shape rather than a
      // guarantee: the table is what this process knows of the topology, and
      // this is what produced the response in front of you.
      { t: 'kv', title: 'The cell that answered', items: cellItems(env.cell) },
      { t: 'kv', title: 'Replication', items: replicationItems(payload['replication']) },
      peerNote(payload['peer']),
      NO_PROMOTION,
    ],
    provenance: provenanceOf(env, CELLS_SOURCE, missing, readAt),
    rowKeys: cells.map((c) => c.id),
  }
}

// ---------------------------------------------------------------------------
// pl-workers
// ---------------------------------------------------------------------------

const WORKERS_SOURCE = 'postgres · worker run history (GET /v1/admin/platform/workers)'

interface WorkerRow {
  readonly kind: string
  readonly family: string
  readonly path: string
  readonly consequence: string
  readonly registered: boolean | null
  readonly availability: Availability | null
  readonly reason: string
  readonly state: string
  readonly lastHeartbeat: string
  readonly heartbeatAge: number | null
  readonly runs: number | null
  readonly rowsExamined: number | null
  readonly lastError: string
  readonly severity: string
  readonly needsAttention: boolean | null
}

function readWorker(raw: unknown): WorkerRow | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const w = raw as Record<string, unknown>
  const kind = text(w['kind'])
  if (kind === '') return null
  return {
    kind,
    family: text(w['family']),
    path: text(w['path']),
    consequence: text(w['consequence']),
    registered: bool(w['registered']),
    availability: availabilityOf(w['availability']),
    reason: text(w['reason']),
    state: text(w['state']),
    lastHeartbeat: text(w['last_heartbeat_at']),
    heartbeatAge: whole(w['heartbeat_age_seconds']),
    runs: whole(w['runs']),
    rowsExamined: whole(w['rows_examined_lifetime']),
    lastError: text(w['last_error']),
    severity: text(w['severity']),
    needsAttention: bool(w['needs_attention']),
  }
}

/**
 * The state chip.
 *
 * `never_started` is the loud one and never acquires an age — it is the ABSENCE
 * of a run row, and a zero age would put a reconciler nobody deployed beside a
 * healthy idle one. A worker whose availability is not `available` has no state
 * at all and says which of the two absences it is.
 */
function workerStateChip(w: WorkerRow): ChipCell {
  if (w.availability !== 'available') return absence(w.availability, w.reason)
  if (w.state === 'never_started') return ['NEVER STARTED', 'r']
  if (w.state === 'degraded') return ['degraded', 'a']
  if (w.state === 'idle') return ['idle', 'n']
  if (w.state === 'ok') return ['ok', 'g']
  return [w.state === '' ? '— no state' : `unknown: ${w.state}`, 'r']
}

/**
 * The consequence cell, toned by path.
 *
 * `severity` is capped by `path` on the backend precisely so a reporting
 * degradation is never returned at money-path severity: a recognition failure
 * degrades a notification and never delays anything. The console renders that
 * separation rather than colouring by severity alone.
 */
function consequenceCell(w: WorkerRow): Cell {
  const said = w.consequence === '' ? '— not stated' : w.consequence
  if (w.path === '') return said
  return `${w.path} path · ${said}`
}

const WORKER_COLS: readonly Column[] = [
  ['Worker kind', 'm', 'minmax(190px,1.3fr)'],
  ['Runs', 'n', '90px'],
  ['Rows examined', 'n', '124px'],
  ['Last heartbeat', 'm', '160px'],
  ['Age', 'n', '104px'],
  ['State', 'c', '160px'],
  ['Consequence if stopped', 'x', 'minmax(200px,1.4fr)'],
]

function workerRow(w: WorkerRow): readonly Cell[] {
  return [
    w.kind,
    w.runs === null ? '—' : String(w.runs),
    w.rowsExamined === null ? '—' : String(w.rowsExamined),
    w.lastHeartbeat === '' ? '— never' : stamp(w.lastHeartbeat),
    // Never a zero for a worker that never ran. The absence is the finding.
    w.heartbeatAge === null ? '—' : span(w.heartbeatAge),
    workerStateChip(w),
    consequenceCell(w),
  ]
}

function standbyNote(cell: CellFacts | null, note: string): Block {
  return {
    t: 'note',
    tone: 'n',
    kicker: 'Correct, not an outage',
    title: 'This cell runs no workers, by design',
    body: `A standby runs none — that is the configuration, not a failure, and the platform status stays ok. ${note === '' ? '' : `The backend adds: ${note}`}`,
    ref: cell === null ? 'the answering cell was not stated' : `${cell.id} · ${cell.role}`,
  }
}

function noWorkersOnPrimary(): Block {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'On a primary this is an outage',
    title: 'No worker kind reported, on a cell that is supposed to run them',
    body: 'This cell says it runs workers and named none. Every worker kind is declared, so an empty list is not "nothing is scheduled" — it is the history not answering, or nothing having started. Both are incidents on a primary.',
    ref: 'the cell indicator above says which kind of cell you are looking at',
  }
}

export function composeWorkers(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const env = envelopeOf(payload)
  const raw = Array.isArray(payload['workers']) ? (payload['workers'] as readonly unknown[]) : []
  const workers: WorkerRow[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readWorker(entry)
    if (parsed === null) unreadable += 1
    else workers.push(parsed)
  }

  const runsWorkers = bool(payload['runs_workers'])
  const neverStarted = workers.filter((w) => w.state === 'never_started').length
  const attention = workers.filter((w) => w.needsAttention === true).length

  const table: TableBlock = {
    t: 'table',
    title: env.cell === null ? 'Workers' : `Workers · ${env.cell.id} (${env.cell.role})`,
    src: 'ledger',
    facets: false,
    cols: WORKER_COLS,
    rows: workers.map(workerRow),
    footer: `${String(workers.length)} worker kind(s)${neverStarted === 0 ? '' : ` · ${String(neverStarted)} never started`}${attention === 0 ? '' : ` · ${String(attention)} needing attention`}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read`}`,
    foot2:
      'A worker that never started has no heartbeat age, and never acquires one: the absence of a run row is the finding, and a zero age would put a reconciler nobody deployed beside a healthy idle one. The consequence column separates money-path workers from reporting-path ones — severity is capped by path, so a reporting degradation is never reported at money-path severity.',
  }

  const blocks: Block[] = [
    statusNote(env.status, env.cell, 'This is worker liveness per declared kind.'),
    table,
  ]

  if (runsWorkers === false) blocks.push(standbyNote(env.cell, text(payload['note'])))
  else if (runsWorkers === true && workers.length === 0 && unreadable === 0) {
    blocks.push(noWorkersOnPrimary())
  }

  const errored = workers.filter((w) => w.lastError !== '')
  if (errored.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Last error',
      title: `${String(errored.length)} worker kind(s) recorded an error on their last pass`,
      body: 'Carried verbatim rather than summarised. A worker can be in state ok and still have failed its last pass — the state is about liveness and the error is about what happened.',
      ref: errored.map((w) => `${w.kind}: ${w.lastError}`).join(' · '),
    })
  }

  const missing: string[] = []
  if (runsWorkers === null) missing.push('whether this cell is supposed to run workers')
  if (env.cell === null) missing.push('which cell answered')

  return {
    blocks,
    provenance: provenanceOf(env, WORKERS_SOURCE, missing, readAt),
    rowKeys: workers.map((w) => w.kind),
  }
}

// ---------------------------------------------------------------------------
// pl-outbox
// ---------------------------------------------------------------------------

const OUTBOX_SOURCE = 'postgres · outbox (GET /v1/admin/platform/outbox)'

interface Destination {
  readonly name: string
  readonly availability: Availability | null
  readonly reason: string
  readonly depth: number | null
  readonly buckets: readonly (readonly [string, number])[]
  readonly oldest: string
  readonly lagSeconds: number | null
  readonly severity: string
  readonly degraded: boolean | null
  readonly affected: readonly string[]
  readonly consumers: readonly string[]
  readonly deadAvailability: Availability | null
  readonly deadReason: string
}

function stringList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  return value.filter((v): v is string => typeof v === 'string' && v !== '')
}

function readDestination(raw: unknown): Destination | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const d = raw as Record<string, unknown>
  const name = text(d['name'])
  if (name === '') return null
  const buckets: (readonly [string, number])[] = []
  if (Array.isArray(d['buckets'])) {
    for (const b of d['buckets'] as readonly unknown[]) {
      if (typeof b !== 'object' || b === null || Array.isArray(b)) continue
      const kind = text((b as Record<string, unknown>)['kind'])
      const depth = whole((b as Record<string, unknown>)['depth'])
      if (kind !== '' && depth !== null) buckets.push([kind, depth])
    }
  }
  const dead = typeof d['dead'] === 'object' && d['dead'] !== null ? (d['dead'] as Record<string, unknown>) : {}
  return {
    name,
    availability: availabilityOf(d['availability']),
    reason: text(d['reason']),
    depth: whole(d['depth']),
    buckets,
    oldest: text(d['oldest_enqueued_at']),
    lagSeconds: whole(d['lag_seconds']),
    severity: text(d['severity']),
    degraded: bool(d['degraded']),
    affected: stringList(d['affected_consumers']),
    consumers: stringList(d['consumers']),
    deadAvailability: availabilityOf(dead['availability']),
    deadReason: text(dead['reason']),
  }
}

function depthTone(d: Destination): Tone {
  if (d.severity === 'page') return 'r'
  if (d.severity === 'warn') return 'a'
  if (d.degraded === true) return 'a'
  return 'g'
}

/**
 * The depth chart.
 *
 * Every bucket the backend sends, INCLUDING the ones at zero. A kind omitted at
 * zero renders as an absent series, and an absent series reads as "the shipper
 * is not running" rather than "nothing is queued" — which is the opposite
 * conclusion about the same healthy system.
 */
function depthSeries(destinations: readonly Destination[]): readonly ChartSeries[] {
  const out: ChartSeries[] = []
  const deepest = destinations.reduce((max, d) => Math.max(max, d.depth ?? 0), 0)
  for (const d of destinations) {
    if (d.availability !== 'available' || d.depth === null) {
      const [label] = absence(d.availability, d.reason)
      out.push([d.name, label, '', 0, d.availability === 'not_built' ? 'n' : 'r'])
      continue
    }
    const percent = deepest === 0 ? 0 : Math.round((d.depth / deepest) * 100)
    out.push([d.name, String(d.depth), 'rows', percent, depthTone(d)])
  }
  return out
}

function destinationItems(d: Destination): readonly KvItem[] {
  const items: KvItem[] = [
    [
      `${d.name} · depth`,
      d.depth === null ? absence(d.availability, d.reason)[0] : `${String(d.depth)} rows`,
      d.degraded === true ? 'degraded' : '',
      depthTone(d),
      d.buckets.length === 0
        ? 'No per-kind breakdown was returned for this destination.'
        : d.buckets.map(([kind, depth]) => `${kind}: ${String(depth)}`).join(' · '),
      '',
    ],
    [
      `${d.name} · lag`,
      d.lagSeconds === null ? '— no undelivered row' : span(d.lagSeconds),
      '',
      d.lagSeconds === null ? 'g' : depthTone(d),
      d.lagSeconds === null
        ? 'Derived from the oldest undelivered row and from nothing else. An empty queue has no oldest row, so there is no lag — this is not a lag of zero.'
        : `Oldest undelivered row: ${d.oldest === '' ? 'not stated' : stamp(d.oldest)}.`,
      '',
    ],
    [
      `${d.name} · goes stale`,
      d.affected.length === 0
        ? d.consumers.length === 0
          ? '— no consumers named'
          : 'nothing — this destination is current'
        : d.affected.join(', '),
      '',
      d.affected.length === 0 ? 'g' : 'a',
      d.consumers.length === 0
        ? 'The backend named no consumers for this destination.'
        : `Consumers: ${d.consumers.join(', ')}.`,
      '',
    ],
    [
      `${d.name} · dead letters`,
      // The schema cannot say zero here, and neither can this console.
      absence(d.deadAvailability, d.deadReason)[0],
      '',
      'n',
      'Shipping is claim-and-delete in one transaction, so a batch that fails to deliver rolls back and its rows stay at the head of the queue. There is no terminal state for a row to reach, and a zero would assert that nothing has ever failed permanently.',
      '',
    ],
  ]
  return items
}

const LAW_20: Block = {
  t: 'note',
  tone: 'a',
  kicker: 'Law 20',
  title: 'A deep outbox is stale reporting, and never an outage',
  body: 'If the reporting mirror is unreachable for a week the queue grows, reporting goes stale, and not one money write is affected — that is the designed failure. A deep queue is drawn as a degraded-data notice on the screens that read from it, never as a service-down state, never as a reason to block a money screen, and it is deliberately not wired into the readiness probe: a probe that fails on a queue depth takes a cell out for a reporting problem.',
  ref: 'the consumers named above are the screens that go stale, and only those',
}

export function composeOutbox(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const env = envelopeOf(payload)
  const raw = Array.isArray(payload['destinations']) ? (payload['destinations'] as readonly unknown[]) : []
  const destinations: Destination[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readDestination(entry)
    if (parsed === null) unreadable += 1
    else destinations.push(parsed)
  }

  const stale = destinations.flatMap((d) => d.affected)
  const blocks: Block[] = [
    statusNote(
      env.status,
      env.cell,
      stale.length === 0
        ? 'No destination reports a consumer going stale.'
        : `Going stale: ${[...new Set(stale)].join(', ')}.`,
    ),
    {
      t: 'chart',
      title: 'Depth by destination',
      src: 'ledger',
      note: 'not a health check',
      series: depthSeries(destinations),
      note2:
        'Every payload kind is carried, including the ones at zero — a kind omitted at zero renders as an absent series, which reads as "the shipper is not running" rather than "nothing is queued".',
    },
  ]

  for (const d of destinations) {
    blocks.push({ t: 'kv', title: `${d.name}`, items: destinationItems(d) })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} destination(s) could not be read`,
      body: 'Counted rather than dropped. A destination missing from this screen is a queue nobody is watching, and the screens it feeds would go stale with no notice drawn on them.',
      ref: 'the shape of those entries is not one this console knows',
    })
  }

  if (destinations.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Outbox',
      title: 'No destination was reported at all',
      body: 'This is not an empty queue. Destinations are declared, so a response naming none means the shipper’s registry did not answer — and an empty screen here reads as "everything is drained", which is the most reassuring possible rendering of not knowing.',
      ref: 'an empty destination list is different from a destination at depth zero',
    })
  }

  blocks.push(LAW_20)

  return {
    blocks,
    provenance: provenanceOf(env, OUTBOX_SOURCE, env.cell === null ? ['which cell answered'] : [], readAt),
    rowKeys: destinations.map((d) => d.name),
  }
}

// ---------------------------------------------------------------------------
// pl-jobs
// ---------------------------------------------------------------------------

const JOBS_SOURCE = 'postgres · job run history (GET /v1/admin/platform/jobs)'

interface JobRun {
  readonly id: number | null
  readonly kind: string
  readonly family: string
  readonly path: string
  readonly actor: string
  readonly startedAt: string
  readonly finishedAt: string
  readonly durationSeconds: number | null
  readonly rowsTouched: number | null
  readonly outcome: string
  readonly error: string
}

function readRun(raw: unknown): JobRun | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  const kind = text(r['kind'])
  if (kind === '') return null
  return {
    id: whole(r['id']),
    kind,
    family: text(r['family']),
    path: text(r['path']),
    actor: text(r['actor']),
    startedAt: text(r['started_at']),
    finishedAt: text(r['finished_at']),
    durationSeconds: whole(r['duration_seconds']),
    rowsTouched: whole(r['rows_touched']),
    outcome: text(r['outcome']),
    error: text(r['error']),
  }
}

function outcomeChip(r: JobRun): ChipCell {
  if (r.outcome === 'succeeded') return ['succeeded', 'g']
  if (r.outcome === 'failed') return ['FAILED', 'r']
  if (r.outcome === 'running') return ['running', 'a']
  return [r.outcome === '' ? '— no outcome' : `unknown: ${r.outcome}`, 'r']
}

const JOB_COLS: readonly Column[] = [
  ['Run', 'm', '90px'],
  ['Job', 'm', 'minmax(200px,1.4fr)'],
  ['Started', 'm', '160px'],
  ['Duration', 'n', '110px'],
  ['Rows touched', 'n', '124px'],
  ['Started by', 'x', '150px'],
  ['Outcome', 'c', '130px'],
]

function runRow(r: JobRun): readonly Cell[] {
  return [
    r.id === null ? '— no id' : String(r.id),
    r.path === '' ? r.kind : `${r.kind} (${r.path})`,
    r.startedAt === '' ? '— not returned' : stamp(r.startedAt),
    // Absent on a run that never finished. Zero would read as "took no time".
    r.durationSeconds === null ? '—' : span(r.durationSeconds),
    // Absent on a run that never finished. Zero would read as "examined
    // nothing" for a run that simply did not get there.
    r.rowsTouched === null ? '—' : String(r.rowsTouched),
    // `scheduler` is a valid actor and the only one this surface can produce
    // today. It renders as an actor rather than as a blank.
    r.actor === '' ? '— unattributed' : r.actor,
    outcomeChip(r),
  ]
}

/**
 * The coverage note — the honest part of this screen.
 *
 * Some declared worker kinds write a run row on every pass, including passes
 * that find nothing; for those, a missing row means the job did not run. The
 * others write no row at all, and an absent row for one of them is NOT evidence
 * that it did not run. A console that made an operator read the API reference to
 * learn which half it was looking at would render the half it received as the
 * whole.
 */
function coverageNote(raw: unknown): Block {
  const c = typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const recorded = stringList(c['recorded'])
  const notRecorded: string[] = []
  if (Array.isArray(c['not_recorded'])) {
    for (const entry of c['not_recorded'] as readonly unknown[]) {
      if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) continue
      const kind = text((entry as Record<string, unknown>)['kind'])
      const reason = text((entry as Record<string, unknown>)['reason'])
      if (kind !== '') notRecorded.push(reason === '' ? kind : `${kind} (${reason})`)
    }
  }

  if (recorded.length === 0 && notRecorded.length === 0) {
    return {
      t: 'note',
      tone: 'r',
      kicker: 'Coverage',
      title: 'This history does not say what it covers',
      body: 'Without a coverage statement there is no way to tell whether a job absent from the table did not run or simply writes no row. Treat the table as the runs that were recorded, and nothing more.',
      ref: 'the backend returned no coverage block',
    }
  }

  return {
    t: 'note',
    tone: notRecorded.length === 0 ? 'n' : 'a',
    kicker: 'Coverage',
    title:
      notRecorded.length === 0
        ? 'Every declared job kind writes a run row'
        : `${String(notRecorded.length)} job kind(s) write no run row at all`,
    body:
      notRecorded.length === 0
        ? 'So a kind absent from this table did not run in the window shown. That is what makes an empty history readable as a scheduler problem rather than as an unknown.'
        : 'For those kinds, an absent row is NOT evidence that the job did not run — nothing was ever going to be written. For the recorded kinds a row is written on every pass, including passes that find nothing, so there a missing row does mean the job did not run. The two halves are not distinguishable from the table alone.',
    ref:
      notRecorded.length === 0
        ? `recorded: ${recorded.join(', ')}`
        : `writes no row: ${notRecorded.join(' · ')}`,
  }
}

export function composeJobs(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const env = envelopeOf(payload)
  const raw = Array.isArray(payload['runs']) ? (payload['runs'] as readonly unknown[]) : []
  const runs: JobRun[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readRun(entry)
    if (parsed === null) unreadable += 1
    else runs.push(parsed)
  }

  const failed = runs.filter((r) => r.outcome === 'failed')
  const table: TableBlock = {
    t: 'table',
    title: 'Job runs',
    src: 'ledger',
    facets: false,
    cols: JOB_COLS,
    rows: runs.map(runRow),
    footer: `${String(runs.length)} recorded run(s)${failed.length === 0 ? '' : ` · ${String(failed.length)} failed`}${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'Duration and rows touched are absent on a run that never finished, never zero: a zero would read as "took no time and examined nothing" for a run that did not get there. Every row names an actor, and the scheduler is an actor.',
  }

  const blocks: Block[] = [
    statusNote(env.status, env.cell, 'This is the recorded job history, newest first.'),
    table,
  ]

  if (failed.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Failed',
      title: `${String(failed.length)} run(s) failed`,
      body: 'The error is carried verbatim rather than summarised — a failure that has been paraphrased is one somebody has to go and look up anyway.',
      ref: failed.map((r) => `${r.kind}: ${r.error === '' ? 'no error text' : r.error}`).join(' · '),
    })
  }

  if (runs.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Job runs',
      title: 'No run was recorded in this window',
      body: 'For the kinds that write a row on every pass, that means they did not run — which is a scheduler problem. For the kinds that write no row at all it means nothing. The coverage statement below says which is which.',
      ref: 'an empty history is only readable alongside its coverage',
    })
  }

  blocks.push(coverageNote(payload['coverage']))

  return {
    blocks,
    provenance: provenanceOf(env, JOBS_SOURCE, env.cell === null ? ['which cell answered'] : [], readAt),
    rowKeys: runs.map((r) => (r.id === null ? null : String(r.id))),
  }
}

// ---------------------------------------------------------------------------
// pl-build
// ---------------------------------------------------------------------------

const BUILD_SOURCE = 'go-wallet · build identity and schema version (GET /v1/admin/platform/build)'

function buildItems(payload: Record<string, unknown>, env: Envelope): readonly KvItem[] {
  const backend = typeof payload['backend'] === 'object' && payload['backend'] !== null
    ? (payload['backend'] as Record<string, unknown>)
    : {}
  const schema = typeof payload['schema'] === 'object' && payload['schema'] !== null
    ? (payload['schema'] as Record<string, unknown>)
    : {}
  const agreement = typeof payload['agreement'] === 'object' && payload['agreement'] !== null
    ? (payload['agreement'] as Record<string, unknown>)
    : {}
  const localState = typeof payload['local_state'] === 'object' && payload['local_state'] !== null
    ? (payload['local_state'] as Record<string, unknown>)
    : {}

  const schemaAvailability = availabilityOf(schema['availability'])
  const incomplete = whole(schema['incomplete'])
  const across = text(agreement['across_cells'])
  const expected = text(localState['expected'])

  const items: KvItem[] = [
    [
      'service',
      text(backend['service']) === '' ? '— not stated' : text(backend['service']),
      '',
      'n',
      'What is answering this console.',
      'm',
    ],
    [
      'build',
      text(backend['build']) === '' ? '— not stated' : text(backend['build']),
      '',
      'n',
      'What this cell is actually running, as the binary asserts about itself.',
      'm',
    ],
    [
      'environment',
      text(backend['environment']) === '' ? '— not stated' : text(backend['environment']),
      '',
      text(backend['environment']) === 'production' ? 'a' : 'n',
      'Stated by the composition root, never inferred from a hostname.',
      'm',
    ],
  ]

  if (schemaAvailability === 'available') {
    const version = whole(schema['version'])
    items.push([
      'schema version',
      version === null ? '— not stated' : String(version),
      '',
      'n',
      `${text(schema['name']) === '' ? 'no migration name' : text(schema['name'])}${text(schema['applied_at']) === '' ? '' : ` · applied ${stamp(text(schema['applied_at']))}`}`,
      'm',
    ])
    items.push([
      'migrations started and never finished',
      incomplete === null ? '— not stated' : String(incomplete),
      incomplete === null ? 'unread' : incomplete === 0 ? 'clean' : 'HALF-APPLIED',
      incomplete === null ? 'r' : incomplete === 0 ? 'g' : 'r',
      'Non-zero means this cell’s schema is half-applied: it answers every read and fails whichever write the unfinished half was for — which is the failure that looks healthy from the outside.',
      '',
    ])
  } else {
    const [label, tone] = absence(schemaAvailability, text(schema['reason']))
    items.push(['schema version', label, '', tone, 'The applied schema could not be read from this cell.', ''])
  }

  items.push([
    'agreement across cells',
    across === '' ? '— not stated' : across,
    across === 'drifted' ? 'DRIFTED' : across === 'agreed' ? 'agreed' : across === 'unknown' ? 'unknown' : '',
    across === 'drifted' ? 'r' : across === 'agreed' ? 'g' : 'a',
    across === 'unknown'
      ? 'Honestly unknown, and a field rather than something this console derives: the process that answered knows its own cell and has no synchronous path to the other one. Asking would be the cross-cell call the design refuses. A console that polls both cells compares what each asserts about itself.'
      : `${text(agreement['reason']) === '' ? 'no reason given' : text(agreement['reason'])}`,
    '',
  ])

  items.push([
    'local state expected',
    expected === '' ? '— not stated' : expected,
    expected === 'none' ? 'none' : '',
    expected === 'none' ? 'g' : 'r',
    expected === 'none'
      ? `Asserted rather than implied: ${text(localState['reason']) === '' ? 'a service writing generated artefacts to local disk looks perfectly healthy right up to the failover where the statements are gone' : text(localState['reason'])}.`
      : 'This cell claims to expect local state, which contradicts the design.',
    '',
  ])

  items.push([
    'answering cell',
    env.cell === null ? '— not stated' : `${env.cell.id} · ${env.cell.role}`,
    '',
    'n',
    'Every platform response carries the cell that produced it.',
    'm',
  ])

  return items
}

const NO_SECRETS: Block = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Law 11',
  title: 'No connection string, credential or key is on this screen',
  body: 'This response is exactly the generic config shape a credential escapes through, so its key set is closed on the backend and asserted by a field-allow-list test — a field cannot be added without failing that test first. Nothing here renders a value, a masked value, or a reveal toggle.',
  ref: 'build identity and schema version only',
}

export function composeBuild(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const env = envelopeOf(payload)
  const blocks: Block[] = [
    statusNote(env.status, env.cell, 'This is what the answering cell says it is running.'),
    { t: 'kv', title: 'Deployed', items: buildItems(payload, env) },
    NO_SECRETS,
  ]
  return {
    blocks,
    provenance: provenanceOf(env, BUILD_SOURCE, env.cell === null ? ['which cell answered'] : [], readAt),
  }
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

function unreadableScreen(what: string, source: string, reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: what,
      title: `${what} could not be read`,
      body: `${reason} Nothing below is known — and on a monitoring screen an unread state is the one that must never render as a healthy one. This is not "everything is fine"; it is "nobody answered".`,
      ref: decisionRef === null
        ? 'Platform Ops is Engineering’s and Admin’s. A refusal here is expected for other roles.'
        : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

function isObject(payload: unknown): payload is Record<string, unknown> {
  return typeof payload === 'object' && payload !== null && !Array.isArray(payload)
}

function platformLoader(
  what: string,
  path: string,
  source: string,
  compose: (payload: Record<string, unknown>, readAt: Date) => ScreenData,
): ScreenLoader {
  return async (api: ApiClient) => {
    let payload: unknown
    try {
      payload = await api.get<unknown>(path)
    } catch (error) {
      if (error instanceof ApiError) {
        return unreadableScreen(
          what,
          source,
          `The endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
          error.decisionRef,
        )
      }
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      return unreadableScreen(what, source, 'The backend could not be reached.', null)
    }
    if (!isObject(payload)) {
      return unreadableScreen(what, source, 'The endpoint returned a shape this console does not know.', null)
    }
    return compose(payload, new Date())
  }
}

export const PLATFORM_CELLS_PATH = '/platform/cells'
export const PLATFORM_WORKERS_PATH = '/platform/workers'
export const PLATFORM_OUTBOX_PATH = '/platform/outbox'
export const PLATFORM_JOBS_PATH = '/platform/jobs'
export const PLATFORM_BUILD_PATH = '/platform/build'

export const loadPlatformCells = platformLoader('Cells & roles', PLATFORM_CELLS_PATH, CELLS_SOURCE, composeCells)
export const loadPlatformWorkers = platformLoader('Worker health', PLATFORM_WORKERS_PATH, WORKERS_SOURCE, composeWorkers)
export const loadPlatformOutbox = platformLoader('Outbox monitor', PLATFORM_OUTBOX_PATH, OUTBOX_SOURCE, composeOutbox)
export const loadPlatformJobs = platformLoader('Job runs', PLATFORM_JOBS_PATH, JOBS_SOURCE, composeJobs)
export const loadPlatformBuild = platformLoader('Build & version', PLATFORM_BUILD_PATH, BUILD_SOURCE, composeBuild)
